import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { PageCTA } from '../components/PageCTA';
const steps = [{
  number: '01',
  title: 'Contexto y enfoque',
  description: 'Recogemos lo esencial: negocio, prioridades, audiencia y material disponible. Definimos qué debe entender una persona al llegar.'
}, {
  number: '02',
  title: 'Estructura y dirección visual',
  description: 'Ordenamos contenidos y construimos una dirección de diseño que haga visible el carácter de tu marca sin sacrificar claridad.'
}, {
  number: '03',
  title: 'Diseño y desarrollo',
  description: 'Convertimos la propuesta en una experiencia responsive, cuidada al detalle y preparada para funcionar en cada pantalla.'
}, {
  number: '04',
  title: 'Revisión compartida',
  description: 'Revisamos contigo lo importante para que el resultado represente correctamente a tu negocio antes de publicar.'
}, {
  number: '05',
  title: 'Publicación y continuidad',
  description: 'Lanzamos el proyecto y dejamos una base clara para acompañar los cambios que necesites después.'
}];
const commitments = [['Una conversación directa', 'Menos capas entre tu necesidad y las decisiones del proyecto.'], ['Un sistema entendible', 'Cada fase tiene un propósito, entregables definidos y una dirección concreta.'], ['Decisiones con criterio', 'Priorizamos lo que ayuda a explicar, orientar y generar confianza.']];
export function ProcessPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#0a0a0a] pt-24" data-id="element-574" data-mp="2iyuoa8cqqhtu">
      <section className="border-b border-white/10 py-16 sm:py-24" data-id="element-575" data-mp="2nuifsuep3n2i">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-576" data-mp="17hl57mxixrt2">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-577" data-mp="17jeg5ef66y6v">
            <div data-id="element-578" data-mp="jl6ot74efn4p">
              <p className="eyebrow" data-id="element-579" data-mp="jkouig1arizf">Proceso</p>
              <h1 className="mt-5 text-4xl font-medium tracking-[-0.055em] text-white sm:text-6xl" data-id="element-580" data-mp="emwsgt6xughi">
                Una ruta clara para llegar a algo bien resuelto.
              </h1>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-neutral-400 sm:text-lg" data-id="element-581" data-mp="4ophqs38suny">
              Un proceso breve y estructurado evita que el proyecto se convierta
              en una sucesión de decisiones desconectadas.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-shell border-b border-white/10" data-id="element-582" data-mp="1v15781lgvlil">
        <div className="container mx-auto max-w-5xl px-6 md:px-12" data-id="element-583" data-mp="1v0dhv8igqk0r">
          <ol className="border-l border-white/10" data-id="element-584" data-mp="1q33mzdcb1g5j">
            {steps.map((step, index) => <motion.li key={step.number} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="relative grid gap-5 border-b border-white/10 py-8 pl-8 last:border-b-0 sm:grid-cols-[110px_1fr] sm:gap-8 sm:pl-12" data-id="element-585" data-mp="1xkjoxp2diqlj" data-mpi={index}>
                <span className="absolute -left-[5px] top-10 h-2.5 w-2.5 bg-white" aria-hidden="true" data-id="element-586" data-mp="1on987o6se998" />
                <p className="text-[11px] font-medium tracking-[0.22em] text-neutral-600" data-id="element-587" data-mp="2zza4rp9w71zj">
                  {step.number}
                </p>
                <div data-id="element-588" data-mp="32khvgyovx3dn">
                  <h2 className="text-xl font-medium tracking-[-0.025em] text-white" data-id="element-589" data-mp="32jydxj35ch05">
                    {step.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-590" data-mp="2xmy735yh3a7z">
                    {step.description}
                  </p>
                </div>
              </motion.li>)}
          </ol>
        </div>
      </section>

      <section className="section-shell border-b border-white/10 bg-black" aria-labelledby="commitments-title" data-id="element-591" data-mp="3liu50ljpkf9r">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-592" data-mp="3gjhglwd6v052">
          <p className="eyebrow" data-id="element-593" data-mp="3v9ovk23zm110">Cómo trabajamos</p>
          <h2 id="commitments-title" className="mt-5 max-w-2xl text-3xl font-medium tracking-[-0.045em] text-white sm:text-4xl" data-id="element-594" data-mp="3vb893ky9dxz3">
            El proceso también es parte de la experiencia.
          </h2>
          <div className="mt-10 grid overflow-hidden border border-white/10 md:grid-cols-3" data-id="element-595" data-mp="43dysi60fmfh">
            {commitments.map(([title, description]) => <div key={title} className="p-7 border-b border-white/10 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0" data-id="element-596" data-mp="mrxtr7vihirq">
                <CheckIcon size={17} className="text-white" aria-hidden="true" data-id="element-597" data-mp="dpop2ymxcjm9" />
                <h3 className="mt-8 text-base font-medium text-white" data-id="element-598" data-mp="40wz2w1fc9dz">
                  {title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-599" data-mp="8rwrpp3ig5ao">
                  {description}
                </p>
              </div>)}
          </div>
        </div>
      </section>
      <PageCTA data-id="element-600" data-mp="3v66k09ak9xgq" />
    </div>;
}